import { Button } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { IncomeGranularity } from "../dto.js";

/**
 * Year or month, and how many months.
 *
 * YEAR IS THE DEFAULT because "is it going up" is the question the module was built for, and several
 * years of gradual growth read as a shape rather than as arithmetic. The month view is where a single
 * year decomposes — and the only place vakantiegeld and the 13th month are visible as the spikes they
 * are, rather than absorbed into an annual total.
 *
 * The range selector belongs to the month view alone: the year view is bounded by RECORDED DATA, from
 * the household's earliest income through today, so there is no count to choose.
 */
export interface ChartControlsProps {
  granularity: IncomeGranularity;
  months: number;
  onGranularity: (value: IncomeGranularity) => void;
  onMonths: (value: number) => void;
}

export function ChartControls({
  granularity,
  months,
  onGranularity,
  onMonths,
}: ChartControlsProps): ReactNode {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div role="group" aria-label={t("inc.chart.resolution")} className="flex gap-1">
        {(["year", "month"] as const).map((value) => (
          <Button
            key={value}
            variant={granularity === value ? "primary" : "outline"}
            aria-pressed={granularity === value}
            onClick={() => onGranularity(value)}
          >
            {value === "year" ? t("inc.chart.byYear") : t("inc.chart.byMonth")}
          </Button>
        ))}
      </div>

      {granularity === "month" ? (
        <div role="group" aria-label={t("inc.chart.monthsShown")} className="flex gap-1">
          {[12, 24, 36].map((value) => (
            <Button
              key={value}
              variant={months === value ? "primary" : "outline"}
              aria-pressed={months === value}
              onClick={() => onMonths(value)}
            >
              {value}m
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
